import React from 'react';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import { Link } from 'ui/base/link';
import { ModalStore } from 'ui/base/modal';
import { LinkClickEvent } from 'ui/base/types';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { ShieldIcon, ShieldSize } from 'ui/base/shield_icon/shield_icon';

import { Optional } from 'services/base/types';

import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { Pangolin } from 'services/accounts/types';
import { CoreBasePath } from 'services/base/page_path';

import baseStyles from './pangolin_selector.module.css';

type PangolinSelectorStyles = {
  container: string;
  modal: string;
  visible: string;
  backdrop: string;
  modalBody: string;
  modalBodyPopIn: string;
  modalContent: string;
  row: string;
  option: string;
  selected: string;
  nickname: string;
};

const themedStyles: ThemedStyles<PangolinSelectorStyles> = {
  [Theme.DAYLIGHT]: {
    container: baseStyles.container,
    modal: baseStyles.modal,
    visible: baseStyles.visible,
    backdrop: baseStyles.backdrop,
    modalBody: baseStyles.daylightModalBody,
    modalBodyPopIn: baseStyles.baseModalBodyPopIn,
    modalContent: baseStyles.modalContent,
    row: baseStyles.row,
    option: baseStyles.daylightOption,
    selected: baseStyles.daylightSelected,
    nickname: baseStyles.nickname,
  },
  [Theme.MIDNIGHT]: {
    container: baseStyles.container,
    modal: baseStyles.modal,
    visible: baseStyles.visible,
    backdrop: baseStyles.backdrop,
    modalBody: baseStyles.midnightModalBody,
    modalBodyPopIn: baseStyles.baseModalBodyPopIn,
    modalContent: baseStyles.modalContent,
    row: baseStyles.row,
    option: baseStyles.midnightOption,
    selected: baseStyles.midnightSelected,
    nickname: baseStyles.nickname,
  },
};

type PangolinOptionProps = {
  active: Pangolin;
  target: Pangolin;
  location: CoreBasePath;
  store: ModalStore;
};

const PangolinOption = observer(({ active, target, location, store }: PangolinOptionProps) => {
  const styles = useStyles(themedStyles);

  const chosen = target.id === active.id;
  const path = { kind: location, pangolinID: target.id };
  const className = classNames(chosen ? styles.selected : styles.option);

  const onClick = React.useCallback((event: LinkClickEvent) => {
    if (chosen) event.preventDefault();
    store.hideModal();
  }, [chosen, store]);

  return (
    <Link path={path} className={className} onClick={onClick} replace>
      <ShieldIcon
        colorCode={target.colorCode}
        onlineStatus={target.onlineStatus}
        size={ShieldSize.SMALL}/>
      <div className={styles.nickname}>
        {target.nickname}
      </div>
    </Link>
  );
});


function chunkAndPad<T>(objects: T[], chunkSize: number): Optional<T>[][] {
  const chunks: Optional<T>[][] = [];
  let curr: Optional<T>[] = [];
  for (let i=0, j=0; i<objects.length || j !== 0; i++, j = (j + 1) % chunkSize) {
    if (j === 0) {
      curr = [];
      chunks.push(curr);
    }
    if (i < objects.length) {
      curr.push(objects[i]);
    } else {
      curr.push(undefined);
    }
  }
  return chunks;
}

type PangolinSelectorChoicesProps = {
  pangolin: Pangolin;
  location: CoreBasePath;
  store: ModalStore;
};

const PangolinSelectorChoices = observer(({ pangolin, location, store }: PangolinSelectorChoicesProps) => {
  const accounts = useAccountsDomain();
  const styles = useStyles(themedStyles);
  const selectorRows = chunkAndPad(accounts.pangolins.values, 4);
  return (
    <>
      {selectorRows.map((items, rowNumber) => (
        <div className={styles.row} key={rowNumber}>
          {items.map((target, index) => (
            target != null ? (
              <PangolinOption
                key={`option-pangolin-${target.id}`}
                active={pangolin}
                target={target}
                location={location}
                store={store}
              />
            ) : (
              <div
                key={`option-blank-${index}`}
                className={styles.option}
              />
            )
          ))}
        </div>
      ))}
    </>
  );
});

export type PangolinSelectorModalProps = {
  pangolin: Pangolin;
  location: CoreBasePath;
  store: ModalStore;
};

export const PangolinSelectorModal = observer(({ pangolin, location, store }: PangolinSelectorModalProps) => {
  const styles = useStyles(themedStyles);
  return <div className={classNames(styles.modal, { [styles.visible]: store.visible })}>
    <div className={styles.backdrop} onClick={store.hideModal}/>
    <div className={classNames(styles.modalBody, { [styles.modalBodyPopIn]: store.visible })}>
      <div className={styles.modalContent}>
        <PangolinSelectorChoices
          pangolin={pangolin}
          location={location}
          store={store}
        />
      </div>
    </div>
  </div>;
});

type BasePangolinSelectorProps = {
  pangolin: Pangolin;
  location: CoreBasePath;
  store: ModalStore;
  children?: React.ReactNode;
};

const BasePangolinSelector = observer(({ pangolin, location, store, children }: BasePangolinSelectorProps) => {
  const styles = useStyles(themedStyles);
  return (
    <>
      <div className={styles.container} onClick={store.showModal}>
        <ShieldIcon
          colorCode={pangolin.colorCode}
          onlineStatus={pangolin.onlineStatus}
          size={ShieldSize.SMALL}
        />
        {children}
      </div>
      <PangolinSelectorModal
        pangolin={pangolin}
        location={location}
        store={store}
      />
    </>
  );
});

type PangolinSelectorProps = {
  pangolin: Pangolin;
  location: CoreBasePath;
  children?: React.ReactNode;
};

export const PangolinSelector = (({ pangolin, location, children }: PangolinSelectorProps) => {
  const [store] = React.useState(() => {
    return new ModalStore(false);
  });

  return (
    <BasePangolinSelector
      pangolin={pangolin}
      location={location}
      store={store}
    >
      {children}
    </BasePangolinSelector>
  );
});
